// Context to manage the edition of Dispatch Scheduler Jobs
import React, { useContext, useEffect, useRef, useState } from 'react';

import { OperatorTruckIndexRecord } from '@isheepdog/operator-search';
import { useList } from '@uidotdev/usehooks';

import { isNil, omit } from 'lodash';

import Confirm from '@/components/Modal/Confirm';

import { useAuthContext } from '@/context/AuthContext';

import { firestore } from '@isd/lib/firebase';
import { Companies, Jobs } from '@isd/lib/firebase/db/collections';
import {
  FakeDocumentSnapshot,
  createFakeDocument,
} from '@isd/lib/firebase/db/fake';
import {
  getCanUserEditAllJobAssignments,
  getCompanyDefaultDispatcherFeeAsPercent,
  getCurrentlyAssignedTrucksCount,
  getJobAssignmentTrucksNumberToReassign,
  getJobAssignmentsThatOverlapNewAssignOperation,
  getJobStartDatetime,
  getUserCompanyId,
  getUserCompanyName,
} from '@isd/lib/firebase/db/helpers';
import {
  getIsFauxOperatorTruck,
  getOperatorTrucksFromJobAssignments,
} from '@isd/lib/firebase/db/helpers/operatorTruckHelpers';
import {
  JobAssignment,
  JobAssignmentDoc,
  JobDoc,
  OperatorTruckDoc,
} from '@isd/lib/firebase/db/metaTypes';
import type {
  AssignmentsUpdateOperation,
  FakeOrRealJobAssignmentDoc,
} from '@isd/lib/firebase/db/operationTypes';
import {
  JobAssignmentsBatchUpdateOperation,
  jobAssignmentsBatchUpdate,
} from '@isd/lib/firebase/httpsCallable';

import { ASSIGNMENT_OVERLAP_CONFLICT_THRESHOLD_MS } from '@isd/lib/constants';
import { getCanUserEditDispatchSchedule } from '@isd/lib/helpers/scheduler';
import { canBrokerDelegateToBroker, isAssigner } from '@isd/lib/helpers/userRoles';

const MIN_TRUCKS_TO_ASSIGN = 1;

type SelectedJob = JobDoc | null;

type SelectedGroup = number | null;

type ParentJobAssignment = JobAssignmentDoc | null;

// FakeOrRealJobAssignmentDoc and AssignmentsUpdateOperation were extracted into
// isd-lib (DSP-188); re-export them for consumers importing from this context.
export type { AssignmentsUpdateOperation, FakeOrRealJobAssignmentDoc };

export interface ConflictInfo {
  operation: AssignmentsUpdateOperation;
  overlappingAssignments: JobAssignmentDoc[];
  jobDoc: JobDoc;
}

interface EditSchedulerContextType {
  canUserEditDispatchSchedule: boolean;
  selectedJob: SelectedJob;
  onSelectJob: (jobDoc: SelectedJob) => void;
  onClearSelectedJob: () => void;
  selectedJobAssignments: JobAssignmentDoc[];
  selectedJobOperatorTrucks: (OperatorTruckDoc | OperatorTruckIndexRecord)[];
  onChangeSelectedJobAssignments: (jobAssignments: JobAssignmentDoc[]) => void;
  selectedGroup: SelectedGroup;
  onSelectGroup: (group: SelectedGroup) => void;
  onSelectOperatorToAssign: (operatorTruck: OperatorTruckIndexRecord) => void;
  onSelectOperatorToRemove: (
    jobAssignmentDoc: JobAssignmentDoc,
    jobDoc?: JobDoc
  ) => void;
  onSelectOperatorToMove: (
    jobAssignmentDoc: JobAssignmentDoc,
    groupIndex: number
  ) => void;
  operationsRecordList: AssignmentsUpdateOperation[];
  isLoading: boolean;
  onConfirmAssignmentChanges: () => Promise<unknown>;
  onRevertAssignmentChanges: () => void;
  parentJobAssignment: ParentJobAssignment;
  onChangeParentJobAssignment: (
    parentJobAssignment: ParentJobAssignment
  ) => void;
  isSelectionDisabled: boolean; // Flag to control if operators can be selected to be assigned
  isConfirmChangesDisabled: boolean; // Flag to control if the changes for currently selected job can be applied
  conflicts: ConflictInfo[];
  isCheckingConflicts: boolean;
}

export const EditSchedulerContext =
  React.createContext<EditSchedulerContextType>({
    canUserEditDispatchSchedule: false,
    selectedJob: null,
    onSelectJob: () => null,
    onClearSelectedJob: () => null,
    selectedJobAssignments: [],
    selectedJobOperatorTrucks: [],
    onChangeSelectedJobAssignments: () => null,
    selectedGroup: null,
    onSelectGroup: () => null,
    onSelectOperatorToAssign: () => null,
    onSelectOperatorToRemove: () => null,
    onSelectOperatorToMove: () => null,
    operationsRecordList: [],
    isLoading: false,
    onConfirmAssignmentChanges: () => Promise.resolve(),
    onRevertAssignmentChanges: () => null,
    parentJobAssignment: null,
    onChangeParentJobAssignment: () => null,
    isSelectionDisabled: false,
    isConfirmChangesDisabled: false,
    conflicts: [],
    isCheckingConflicts: false,
  });

export const useEditSchedulerContext = () => useContext(EditSchedulerContext);

export const EditSchedulerContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { userDoc, userCompanyId } = useAuthContext();
  const [canUserEditDispatchSchedule, setCanUserEditDispatchSchedule] =
    useState(false);
  const [selectedJob, setSelectedJob] = useState<SelectedJob>(null);
  const [selectedJobAssignments, setSelectedJobAssignments] = useState<
    JobAssignmentDoc[]
  >([]);
  const [selectedJobOperatorTrucks, setSelectedJobOperatorTrucks] = useState<
    (OperatorTruckDoc | OperatorTruckIndexRecord)[]
  >([]);
  const [selectedGroup, setSelectedGroup] = useState<SelectedGroup>(null);
  const selectedGroupRef = useRef<SelectedGroup>(null);

  const [operatorToAssign, setOperatorToAssign] =
    useState<OperatorTruckIndexRecord | null>(null);
  const [trucksNumberToReassign, setTrucksNumberToReassign] =
    useState(MIN_TRUCKS_TO_ASSIGN);
  const [assignmentFee, setAssignmentFee] = useState(0);

  const selectedOperatorRef = useRef<OperatorTruckIndexRecord | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [operationsRecordList, manageOperations] =
    useList<AssignmentsUpdateOperation>([]);
  const [parentJobAssignment, setParentJobAssignment] =
    useState<ParentJobAssignment>(null);
  const [isSelectionDisabled, setIsSelectionDisabled] = useState(false);
  // keep count of how many trucks can be reassigned to selected job
  const [remainingSlotsToReassignCount, setRemainingSlotsToReassignCount] =
    useState(Number.MAX_SAFE_INTEGER);
  const [isConfirmChangesDisabled, setIsConfirmChangesDisabled] =
    useState(false);
  const [conflicts, setConflicts] = useState<ConflictInfo[]>([]);
  const [isCheckingConflicts, setIsCheckingConflicts] = useState(false);

  useEffect(() => {
    selectedGroupRef.current = selectedGroup;
  }, [selectedGroup]);

  useEffect(() => {
    selectedOperatorRef.current = operatorToAssign;
  }, [operatorToAssign]);

  useEffect(() => {
    if (!userDoc) {
      return;
    }
    async function detectConflicts() {
      const assignOperations = operationsRecordList.filter(
        (op) => op.type === 'assign'
      );
      if (assignOperations.length === 0 || !userDoc) {
        setConflicts([]);
        setIsCheckingConflicts(false);
        return;
      }
      setIsCheckingConflicts(true);

      const jobIds = [
        ...new Set(assignOperations.map((op) => op.jobId).filter((id) => !!id)),
      ] as string[];
      const jobDocsSnapshots = await Promise.all(
        jobIds.map((id) => firestore.getDoc(Jobs.doc(id)))
      );
      const jobDocsMap = new Map(jobDocsSnapshots.map((doc) => [doc.id, doc]));

      const newConflicts: ConflictInfo[] = [];

      for (const operation of assignOperations) {
        const jobDoc = jobDocsMap.get(operation.jobId);
        if (!jobDoc || !operation.userId) continue;

        // 1. Check against DB
        const dbOverlaps = await getJobAssignmentsThatOverlapNewAssignOperation(
          operation,
          userDoc
        );

        // 2. Check against other new operations
        const operationStartTime = getJobStartDatetime(
          jobDoc,
          operation.groupIndex
        );

        const newOpsOverlaps = assignOperations
          .filter((otherOp) => {
            if (operation === otherOp || operation.userId !== otherOp.userId) {
              return false;
            }
            const otherJobDoc = jobDocsMap.get(otherOp.jobId);
            if (!otherJobDoc) return false;

            const otherOpStartTime = getJobStartDatetime(
              otherJobDoc,
              otherOp.groupIndex
            );
            const timeDiff = Math.abs(
              operationStartTime.getTime() - otherOpStartTime.getTime()
            );

            return timeDiff < ASSIGNMENT_OVERLAP_CONFLICT_THRESHOLD_MS;
          })
          .map((op) => op.jobAssignmentDoc as JobAssignmentDoc);

        const allOverlappingAssignments = [...dbOverlaps, ...newOpsOverlaps];

        if (allOverlappingAssignments.length > 0) {
          newConflicts.push({
            operation,
            overlappingAssignments: allOverlappingAssignments,
            jobDoc,
          });
        }
      }
      setConflicts(newConflicts);
      setIsCheckingConflicts(false);
    }

    detectConflicts();
  }, [operationsRecordList, userDoc]);

  // Update remainingSlotsToReassignCount and isSelectionDisabled
  // when selected job changes or a new operation is added to the list
  useEffect(() => {
    if (!parentJobAssignment || !selectedJob) return;
    const maxTrucksNumberToReassign =
      getJobAssignmentTrucksNumberToReassign(parentJobAssignment);
    const jobAssignmentsParsed = parseJobAssignmentsBasedOnOperationsRecord(
      selectedJob?.id,
      selectedJobAssignments,
      operationsRecordList
    ) as JobAssignmentDoc[];
    const currentlyAssignedTrucksCount = getCurrentlyAssignedTrucksCount({
      parentJobAssignment,
      jobAssignments: jobAssignmentsParsed,
    });
    const remainingSlotsToReassignCount =
      maxTrucksNumberToReassign - currentlyAssignedTrucksCount;
    setRemainingSlotsToReassignCount(remainingSlotsToReassignCount);
    setIsSelectionDisabled(remainingSlotsToReassignCount <= 0);
    // Disable confirm changes and unselect job when user has removed themselves
    // and there are pending slots to assign
    const isConfirmChangesDisabled =
      remainingSlotsToReassignCount > 0 &&
      !jobAssignmentsParsed.some(
        (jobAssignmentDoc) =>
          jobAssignmentDoc.get('receiver.user.id') === userDoc?.id &&
          // Assignment should also be leaf node to not disable confirm changes
          !!jobAssignmentDoc.get('isLeafNode')
      );
    setIsConfirmChangesDisabled(isConfirmChangesDisabled);
  }, [
    selectedJob,
    operationsRecordList.length,
    selectedJobAssignments.length,
    parentJobAssignment,
  ]);

  useEffect(() => {
    if (!userCompanyId) return;

    firestore.getDoc(Companies.doc(userCompanyId)).then((userCompanyDoc) => {
      if (!userCompanyDoc) return;
      const defaultDispatcherFee =
        getCompanyDefaultDispatcherFeeAsPercent(userCompanyDoc);
      setAssignmentFee(defaultDispatcherFee);
    });
  }, [!!userCompanyId]);

  useEffect(() => {
    if (!userDoc) return;
    setCanUserEditDispatchSchedule(getCanUserEditDispatchSchedule(userDoc));

    // Add onClick handler to document body to clear selected job when clicked outside
    document.body.addEventListener('click', (event) => {
      // Do not clear selected job if a group or operator is already selected
      if (
        selectedGroupRef.current !== null ||
        selectedOperatorRef.current !== null ||
        !event.target
      )
        return;
      const target = event.target as Element;
      const isJobCardClicked =
        !!target.closest('.job-card') ||
        !!target.closest('.operator-search-widget') ||
        !!target.closest('.action-btn-panel');
      if (!isJobCardClicked) {
        // FIXME: Need a reference to isConfirmChangesDisabled to read the latest value
        return onClearSelectedJob();
      }
    });
  }, [!!userDoc]);

  useEffect(() => {
    if (selectedJob === null) {
      setSelectedGroup(null);
      setSelectedJobAssignments([]);
      setParentJobAssignment(null);
    }
  }, [selectedJob]);

  function onSelectJob(jobDoc: SelectedJob) {
    setSelectedJob(jobDoc);
  }

  function onClearSelectedJob() {
    // Check that current job assignments are in a valid state
    // before clearing selected job
    if (isConfirmChangesDisabled) return;
    setSelectedJob(null);
  }

  function onSelectGroup(groupIndex: SelectedGroup) {
    if (isConfirmChangesDisabled) return;
    setSelectedGroup(groupIndex);
  }

  function onChangeSelectedJobAssignments(jobAssignments: JobAssignmentDoc[]) {
    getOperatorTrucksFromJobAssignments(jobAssignments).then(
      (operatorTrucks) => {
        setSelectedJobAssignments(jobAssignments);
        setSelectedJobOperatorTrucks(operatorTrucks);
      }
    );
  }

  function onSelectOperatorToAssign(operatorHit: OperatorTruckIndexRecord) {
    if (isSelectionDisabled) return;
    // Check that same operator was not selected twice
    const [lastOperation] = operationsRecordList.slice(-1);
    if (
      !!lastOperation &&
      lastOperation.type === 'assign' &&
      lastOperation.userId === operatorHit.operator.userId &&
      lastOperation.jobId === selectedJob?.id
    ) {
      return console.log(
        `Operator ${operatorHit.operator.nickname} was selected twice`
      );
    }
    const isCurrentUser = userDoc?.id === operatorHit.operator.userId;
    if (
      isAssigner(operatorHit) &&
      !isCurrentUser &&
      !getIsFauxOperatorTruck(operatorHit)
    ) {
      setOperatorToAssign(operatorHit);
      setTrucksNumberToReassign(MIN_TRUCKS_TO_ASSIGN);
    } else {
      // When non assigner or current user is selected add it to job without prompting
      const trucksNumberToReassignNonAssigner = 0;
      onConfirmAssignOperator(operatorHit, trucksNumberToReassignNonAssigner);
    }
  }

  function onConfirmAssignOperator(
    operator?: OperatorTruckIndexRecord,
    trucksNumberToReassign_?: number
  ) {
    // Check if should use values from parameters or from state
    const selectedOperator = operator || operatorToAssign;
    const trucksNumberToReassignFinal = isNil(trucksNumberToReassign_)
      ? trucksNumberToReassign
      : trucksNumberToReassign_;
    if (!selectedJob || !selectedOperator || selectedGroup === null || !userDoc)
      return;
    const isCurrentUser = selectedOperator.operator.userId === userDoc?.id;
    // Check that the same operator was not previously removed from the same group
    const removeSameOperatorOperationIndex = operationsRecordList.findIndex(
      (operation) =>
        operation.jobId === selectedJob.id &&
        operation.type === 'remove' &&
        // TODO: Consider case when operator is moved to another group and then removed
        operation.jobAssignmentDoc?.get('groupIndex') === selectedGroup &&
        selectedOperator.operator.userId ===
          operation.jobAssignmentDoc.get('receiver.user.id') &&
        // Check that trucksNumberToReassign is not being updated
        (operation.jobAssignmentDoc.get('receiver.trucksNumberToReassign') ==
          trucksNumberToReassignFinal ||
          // If selected operator is current user ignore trucksNumberToReassign
          // because it would be defaulted to the value set by assigner
          isCurrentUser)
    );
    if (removeSameOperatorOperationIndex >= 0) {
      manageOperations.removeAt(removeSameOperatorOperationIndex);
    } else {
      const jobAssignmentDoc = selectedJobAssignments.find(
        (jobAssignmentDoc) =>
          jobAssignmentDoc.get('receiver.user.id') ===
          selectedOperator.operator.userId
      );
      manageOperations.push({
        jobId: selectedJob.id,
        type: 'assign',
        userId: selectedOperator.operator.userId,
        groupIndex: selectedGroup,
        trucksNumberToReassign: !isCurrentUser
          ? trucksNumberToReassignFinal
          : jobAssignmentDoc?.get('receiver.trucksNumberToReassign'),
        assignmentFee,
        isReAssignment:
          !!userDoc && !getCanUserEditAllJobAssignments(userDoc, selectedJob),
        jobAssignmentDoc: createFakeDocument<JobAssignment>(
          `${selectedOperator.operator.userId}-${selectedJob.id}-${selectedGroup}`,
          {
            groupIndex: selectedGroup,
            isLeafNode: true,
            parentJobAssignmentId: parentJobAssignment?.id,
            job: selectedJob.data(),
            assigner: {
              company: {
                id: getUserCompanyId(userDoc),
                name: getUserCompanyName(userDoc),
              },
            },
            receiver: {
              company: {
                id: selectedOperator.company.companyId,
                name: selectedOperator.company.name,
              },
              user: {
                id: selectedOperator.operator.userId,
                ...selectedOperator.operator,
              },
              startDatetimeDifferenceMs: 0,
              status: !isCurrentUser
                ? 'not notified'
                : jobAssignmentDoc?.get('receiver.status'),
              trucksNumberToReassign: trucksNumberToReassignFinal,
              trucksAssignedNumber: 0,
            },
          }
        ),
      });
    }
    setOperatorToAssign(null);
    // Add selected operator to list of OperatorTrucks so it's
    // immediately excluded from search results list
    setSelectedJobOperatorTrucks(
      selectedJobOperatorTrucks.concat(selectedOperator)
    );
  }

  function onCancelAssignOperator() {
    setOperatorToAssign(null);
  }

  function onSelectOperatorToRemove(
    jobAssignmentDoc: JobAssignmentDoc,
    jobDoc?: JobDoc
  ) {
    const job = jobDoc ?? selectedJob;
    if (!job) return;
    // Check if operator being removed was previously moved (so it should delete the "move" operation)
    const previousMoveOperationIndex = operationsRecordList.findIndex(
      (operation) =>
        operation.jobId === job.id &&
        operation.type === 'move' &&
        operation.jobAssignmentId === jobAssignmentDoc.id
    );
    if (previousMoveOperationIndex >= 0) {
      manageOperations.removeAt(previousMoveOperationIndex);
    }
    // Check that the same operator being removed was not previously assigned
    const assignSameOperatorOperationIndex = operationsRecordList.findIndex(
      (operation) =>
        operation.type === 'assign' &&
        operation.groupIndex === jobAssignmentDoc.get('groupIndex') &&
        operation.jobAssignmentDoc?.get('receiver.user.id') ===
          jobAssignmentDoc.get('receiver.user.id')
    );
    if (assignSameOperatorOperationIndex >= 0) {
      manageOperations.removeAt(assignSameOperatorOperationIndex);
    } else {
      manageOperations.push({
        jobId: job.id,
        type: 'remove',
        jobAssignmentId: jobAssignmentDoc.id,
        jobAssignmentDoc,
        isReAssignment:
          !!userDoc && !getCanUserEditAllJobAssignments(userDoc, job),
      });
    }
    // Remove OperatorTruck related to removed job assignment from selectedOperatorTrucks
    const updatedSelectedOperatorTrucks = selectedJobOperatorTrucks.filter(
      (operatorTruck) => {
        const operatorTruckUserId =
          'get' in operatorTruck
            ? operatorTruck.get('operator.userId')
            : operatorTruck.operator.userId;
        return operatorTruckUserId !== jobAssignmentDoc.get('receiver.user.id');
      }
    );
    setSelectedJobOperatorTrucks(updatedSelectedOperatorTrucks);
  }

  function onSelectOperatorToMove(
    jobAssignmentDoc: JobAssignmentDoc,
    groupIndex: number
  ) {
    if (!selectedJob) return;
    // Check if there's a previous move operation for same assignment
    const previousMoveOperationIndex = operationsRecordList.findIndex(
      (operation) =>
        operation.jobId === selectedJob.id &&
        operation.type === 'move' &&
        operation.jobAssignmentId === jobAssignmentDoc.id
    );
    if (previousMoveOperationIndex >= 0) {
      manageOperations.removeAt(previousMoveOperationIndex);
      // Check if the new group is the same as it was originally moved from
      const originalJobAssignment = selectedJobAssignments.find(
        (originalJobAssignment) =>
          originalJobAssignment.id === jobAssignmentDoc.id
      );
      const isGoingBackToInitialGroup =
        originalJobAssignment?.get('groupIndex') === groupIndex;
      // If operator is being moved back to its original group return early
      // after removing last operation as there's no need to add a new one
      if (isGoingBackToInitialGroup) return;
    }
    // Check if operator being moved is a new assignment
    const assignOperationIndex = operationsRecordList.findIndex(
      (operation) =>
        operation.jobId === selectedJob.id &&
        operation.type === 'assign' &&
        operation.jobAssignmentDoc?.id === jobAssignmentDoc.id
    );
    const isNewAssignedOperator = assignOperationIndex >= 0;
    if (isNewAssignedOperator) {
      const assignOperationToUpdate =
        operationsRecordList[assignOperationIndex];
      if (!assignOperationToUpdate) return;
      // Update group index of existing assign operation
      manageOperations.updateAt(assignOperationIndex, {
        ...assignOperationToUpdate,
        groupIndex,
        jobAssignmentDoc: createFakeDocument<JobAssignment>(
          `${jobAssignmentDoc.get('receiver.user.id')}-${selectedJob.id}-${groupIndex}`,
          {
            ...(jobAssignmentDoc.data() || {}),
            groupIndex,
          }
        ),
      });
    } else {
      manageOperations.push({
        jobId: selectedJob.id,
        type: 'move',
        jobAssignmentId: jobAssignmentDoc.id,
        groupIndex,
        userId: jobAssignmentDoc.get('receiver.user.id'),
        jobAssignmentDoc,
        isReAssignment:
          !!userDoc && !getCanUserEditAllJobAssignments(userDoc, selectedJob),
      });
    }
  }

  function onConfirmAssignmentChanges() {
    setIsLoading(true);
    onClearSelectedJob();
    const operations: JobAssignmentsBatchUpdateOperation[] =
      operationsRecordList
        .map((operation) => omit(operation, 'jobAssignmentDoc'))
        .map((operation) => {
          if ('groupIndex' in operation) {
            return {
              ...operation,
              // Ensure groupIndex is sent as string
              groupIndex: `${operation.groupIndex}`,
            };
          }
          return operation;
        });
    return jobAssignmentsBatchUpdate({
      operations,
    }).finally(() => {
      setIsLoading(false);
      manageOperations.clear();
    });
  }

  function onRevertAssignmentChanges() {
    onClearSelectedJob();
    manageOperations.clear();
  }

  const contextValue: EditSchedulerContextType = {
    canUserEditDispatchSchedule,
    selectedJob,
    onSelectJob,
    onClearSelectedJob,
    selectedJobAssignments,
    selectedJobOperatorTrucks,
    onChangeSelectedJobAssignments,
    selectedGroup,
    onSelectGroup: onSelectGroup,
    onSelectOperatorToAssign,
    onSelectOperatorToRemove,
    onSelectOperatorToMove,
    operationsRecordList,
    isLoading,
    onConfirmAssignmentChanges,
    onRevertAssignmentChanges,
    parentJobAssignment,
    onChangeParentJobAssignment: setParentJobAssignment,
    isSelectionDisabled,
    isConfirmChangesDisabled,
    conflicts,
    isCheckingConflicts,
  };
  return (
    <EditSchedulerContext.Provider value={contextValue}>
      {children}
      <Confirm
        content={
          !!selectedJob && (
            <ConfirmAssignOperatorContent
              operatorToAssign={operatorToAssign}
              selectedJob={selectedJob}
              trucksNumberToReassign={trucksNumberToReassign}
              onChangeTrucksNumberToReassign={setTrucksNumberToReassign}
              maxTrucksNumberToReassign={remainingSlotsToReassignCount}
            />
          )
        }
        onConfirm={() => onConfirmAssignOperator()}
        onCancel={onCancelAssignOperator}
        isOpen={!!operatorToAssign}
        isLoading={isLoading}
      />
    </EditSchedulerContext.Provider>
  );
};

function ConfirmAssignOperatorContent({
  selectedJob,
  operatorToAssign,
  trucksNumberToReassign,
  onChangeTrucksNumberToReassign,
  maxTrucksNumberToReassign,
}: {
  selectedJob: JobDoc;
  operatorToAssign: OperatorTruckIndexRecord | null;
  trucksNumberToReassign: number;
  onChangeTrucksNumberToReassign: (trucks: number) => void;
  maxTrucksNumberToReassign: number;
}) {
  if (!operatorToAssign) {
    return null;
  }

  // For users than can reassign their pending slots to other assigners
  // ignore the total trucks their company have
  const totalTrucks = canBrokerDelegateToBroker(operatorToAssign)
    ? Number.MAX_SAFE_INTEGER
    : operatorToAssign.company.totalTrucks;

  const maxValue = Math.min(totalTrucks, maxTrucksNumberToReassign);

  const onChange: React.ChangeEventHandler<HTMLInputElement> = (ev) => {
    const trucks = +ev.target.value;
    if (trucks > 0 && trucks <= maxValue) {
      onChangeTrucksNumberToReassign(trucks);
    }
  };

  return (
    <div className="text-center">
      <h3>{`Add "${operatorToAssign.operator.nickname}" to ${selectedJob?.get('name')}`}</h3>
      {isAssigner(operatorToAssign as unknown as OperatorTruckDoc) && (
        <div className="flex items-center justify-evenly">
          <div className="mr-2">{'How many trucks should be assigned?'}</div>
          <input
            type="number"
            className="input w-24"
            min={MIN_TRUCKS_TO_ASSIGN}
            max={maxValue}
            value={trucksNumberToReassign}
            onChange={onChange}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Updates a list of job assignments based on a series of operations (assign, remove, move).
 * This function processes a list of operations (assign, remove, move) applied to job assignments for a specific job.
 * It filters the operations for the given job ID, applies these operations to the initial list of job assignments,
 * and returns an updated list. This updated list may include both real and fake (`FakeDocumentSnapshot`) job
 * assignment documents, reflecting the changes specified by the operations. The function supports adding new
 * assignments, removing existing ones, or moving assignments to a different group within the job.
 *
 * @param {string} jobId - The ID of the job for which job assignments are being updated.
 * @param {JobAssignmentDoc[]} jobAssignments - The initial list of job assignments for the job.
 * @param {AssignmentsUpdateOperation[]} operationsRecordList - A list of operations to apply to the job assignments.
 * @returns {FakeOrRealJobAssignmentDoc[]} An updated list of job assignments after applying the specified operations.
 */
export function parseJobAssignmentsBasedOnOperationsRecord(
  jobId: string,
  jobAssignments: JobAssignmentDoc[],
  operationsRecordList: AssignmentsUpdateOperation[]
) {
  const jobOperationsRecordList = operationsRecordList.filter(
    (operation) => operation.jobId === jobId
  );
  // Exclude fake JobAssignments to avoid duplicated elements
  let parsedJobAssignments: FakeOrRealJobAssignmentDoc[] =
    jobAssignments.filter((jobAssignmentDoc) => !!jobAssignmentDoc.exists);
  jobOperationsRecordList.forEach((operation) => {
    const { type, jobAssignmentDoc, jobAssignmentId, groupIndex } = operation;
    if (type === 'assign' && !!jobAssignmentDoc) {
      parsedJobAssignments = parsedJobAssignments.concat(jobAssignmentDoc);
    } else if (type === 'remove' && !!jobAssignmentId) {
      const jobAssignmentToRemoveId = jobAssignmentId;
      parsedJobAssignments = parsedJobAssignments.filter(
        (jobAssignmentDoc) => jobAssignmentDoc.id !== jobAssignmentToRemoveId
      );
    } else if (type === 'move' && !!jobAssignmentId && groupIndex !== null) {
      const jobAssignmentToMoveIndex = parsedJobAssignments.findIndex(
        (jobAssignmentDoc) => jobAssignmentDoc.id === jobAssignmentId
      );
      if (jobAssignmentToMoveIndex < 0) return;
      const jobAssignmentToMove = parsedJobAssignments[
        jobAssignmentToMoveIndex
      ] as JobAssignmentDoc;
      const receiver = jobAssignmentToMove.get('receiver');

      if (!receiver || !receiver.user || !receiver.company) return;

      parsedJobAssignments = [
        ...parsedJobAssignments.slice(0, jobAssignmentToMoveIndex),
        createFakeDocument<JobAssignment>(jobAssignmentToMove.id, {
          groupIndex: groupIndex,
          isLeafNode: true,
          job: jobAssignmentToMove.get('job'),
          receiver: {
            user: receiver.user,
            company: receiver.company,
            startDatetimeDifferenceMs: receiver.startDatetimeDifferenceMs,
            status: 'not notified', // reset notified status when moving operator from one group to another
            trucksNumberToReassign: receiver.trucksNumberToReassign || 0,
            trucksAssignedNumber: receiver.trucksAssignedNumber || 0,
          },
        }),
        ...parsedJobAssignments.slice(
          jobAssignmentToMoveIndex + 1,
          parsedJobAssignments.length
        ),
      ];
    }
  });
  return parsedJobAssignments;
}
